import React from "react";
import { Section, H2, CheckList } from "../../components/common-components";
import { useSelector, useDispatch } from "react-redux";
import { getCurrentUserDetails } from "../../../../redux/modules/user/actions";
import { PrescriberSuggestionOptions } from "../../../patient/Patient-Menu/Interventions/data";
import { stripHtmlToPlainText } from "../../../../helpers/interventionHtml";

export const PrescriberSuggestionSection = ({
  formType,
  prescriberSuggestions = [],
  pharmacistRxChangeComments,
  suggestFollowUpAppointment = false,
}) => {
    const selected = Array.isArray(prescriberSuggestions)
        ? prescriberSuggestions
        : prescriberSuggestions
        ? [prescriberSuggestions]
        : [];

    const selectedNormalized = new Set(
        selected.map((v) => (v == null ? "" : v.toString().trim()))
    );

    const prescriberOptions = (PrescriberSuggestionOptions || []).map((opt) => ({
        label: opt?.label,
        checked: selectedNormalized.has(opt?.label),
    }));

    const pharmacistOptions = [
        {
            label: "Suggest to schedule appointment with primary provider",
            checked: suggestFollowUpAppointment,
        },
    ];

    const pharmacistRecommendationOptions = (PrescriberSuggestionOptions || []).map((opt) => ({
        label: opt?.label,
        checked: false,
    }));

    const phoneAuthorizationOptions = [
        { label: "Select if verbal / phone authorization", checked: false }
    ];

    const { currentLoggedInUserDetail: userFromCurrentState } = useSelector((state) => state.currentLoggedInUserDetail ?? {});
    const usersState = useSelector((state) => state.users ?? {});
    const { currentLoggedInUserDetail: userFromUsers } = usersState ?? {};
    const currentLoggedInUserDetail = userFromCurrentState ?? userFromUsers ?? {};
    const pharmacistName = [currentLoggedInUserDetail?.firstName, currentLoggedInUserDetail?.lastName]
        .filter(Boolean)
        .join(" ") || "";

    const rxChangeCommentsText =
        pharmacistRxChangeComments == null
            ? ""
            : stripHtmlToPlainText(String(pharmacistRxChangeComments)).trim();
    const hasRxChangeComments = rxChangeCommentsText.length > 0;

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (!currentLoggedInUserDetail || !currentLoggedInUserDetail.firstName) {
            try {
                const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
                const userId = userInfo.userId;
                if (userId) {
                    dispatch(getCurrentUserDetails(userId, false));
                }
            } catch (e) {
                // ignore JSON parse errors
            }
        }
    }, [currentLoggedInUserDetail, dispatch]);

    return (
        <Section formType={formType}>
            <div className="border border-teal-300 bg-gray-10">
                <div className="p-2">
                    <div className="grid grid-cols-2 border-b border-gray-200 pb-1">
                        <div className="pr-4 border-r border-gray-200">
                            <H2 className="text-teal-300">Pharmacist Suggestions for Prescriber</H2>
                        </div>
                        <div className="pl-4">
                            <H2 className="text-teal-300">Prescriber to Select One Option</H2>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 mt-2">
                        <div className="space-y-2 pr-4 border-r border-gray-200">
                            <CheckList list={prescriberOptions} />
                        </div>
                        
                        <div className="space-y-2 pl-4">
                            <CheckList list={pharmacistRecommendationOptions} />
                        </div>
                    </div>
                </div>

                <div className="p-2 border-t border-gray-200">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 items-start">
                        <div className="min-w-0">
                            <H2 className="text-teal-300 text-xs">Pharmacist's Recommendation</H2>
                            {hasRxChangeComments ? (
                                <p className="whitespace-pre-wrap break-words text-[11px] leading-4 text-gray-800">
                                    {rxChangeCommentsText}
                                </p>
                            ) : (
                                <p className="text-gray-800 text-[11px] leading-4">
                                    No additional recommendations at this time
                                </p>
                            )}
                        </div>
                        <div className="mr-[7px] shrink-0">
                            <CheckList list={pharmacistOptions} />
                            {!suggestFollowUpAppointment ? (
                                <p className="ml-6 text-xs text-gray-800 mt-1">
                                    Not applicable at this time
                                </p>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="flex border-t border-gray-200 h-[280px]">
                    <div className="flex-1 p-4">
                        <div className="flex items-start justify-start">
                            <div className="text-4xl font-semibold text-gray-800 ">Rx</div>
                        </div>
                    </div>

                    <div className="w-px bg-gray-200"></div>

                    <div className="flex-1 p-4">
                        <H2 className="text-teal-600 text-sm mb-2">Prescriber Review and Comments</H2>
                        <div className="p-4 h-40  overflow-y-auto"></div>
                        
                        <div className="space-y-0 -mt-2">
                            <div className="flex items-center space-x-2">
                                <span className="text-xs w-32">Prescriber Signature:</span>
                                <div className="flex-1 border-b border-gray-300 h-6"></div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-xs w-32">Prescriber Name:</span>
                                <div className="flex-1 border-b border-gray-300 h-6"></div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-xs w-32">Prescriber Licence:</span>
                                <div className="flex-1 border-b border-gray-300 h-6"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 p-2">
                    <CheckList list={phoneAuthorizationOptions} />
                    <div className="flex items-center space-x-8">
                        <span className="text-xs">Pharmacist Name:</span>
                        <div className="w-52 border-b border-gray-200 h-6 flex items-center">
                            <span className="text-sm font-semibold">{pharmacistName}</span>
                        </div>
                    </div>
                </div>
            </div>

        </Section>
    )
}